import { Injectable } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { LearnerCourseService } from "src/app/_services/learner-course.service";
import {
  GetLearnerCourseLog,
  GetLearnerCourseLogError,
  GetLearnerCourseLogSuccess,
  GetLearnerCoursesList,
  GetLearnerCoursesListError,
  GetLearnerCoursesListSuccess,
  ReAssignLearnerToAssessment,
  ReAssignLearnerToAssessmentError,
  ReAssignLearnerToAssessmentSuccess,
  GenerateLearningPlan,
  GenerateLearningPlanError,
  GenerateLearningPlanSuccess,
  UpdateDisableLearnerCourse,
  UpdateDisableLearnerCourseError,
  UpdateDisableLearnerCourseSuccess,
  GetLearnerCourse,
  GetLearnerCourseSuccess,
  GetLearnerCourseError,
  UpdateLearnerCourse,
  UpdateLearnerCourseSuccess,
  UpdateLearnerCourseError,
  GetLearnersLearnerCourseLogStatus,
  GetLearnersLearnerCourseLogStatusSuccess,
  GetLearnersLearnerCourseLogStatusError,
  AddLearnerCourseList,
  AddLearnerCourseListSuccess,
  AddLearnerCourseListError,
  GetLearnerCourseScreenerResult,
  GetLearnerCourseScreenerSuccess,
  GetLearnerCourseScreenerError,
} from "./learner-course.actions";
import { catchError, map, switchMap } from "rxjs/operators";
import { LearnerCourse, LearnerCoursesList, UpdateLearnerCourseDto } from "src/app/_models/LearnerCourse";
import { of } from "rxjs";
import { HttpErrorResponse } from "@angular/common/http";
import { LearnerCourseLogService } from "src/app/_services/learner-course-log.service";
import { LearnerCourseLog } from "src/app/_models/LearnerCourseLog";
import { Course } from "src/app/_models/Course";

@Injectable()
export class LearnerCourseEffects {
  constructor(
    private action$: Actions,
    private learnerCourseService: LearnerCourseService,
    private learnerCourseLogService: LearnerCourseLogService
  ) {}

  public getLearnerCoursesListEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearnerCoursesList),
      switchMap((payload) =>
        this.learnerCourseService
          .getLearnerCoursesList$(payload.learnerId, payload.langId)
          .pipe(
            map((result: LearnerCoursesList[]) => {
              return GetLearnerCoursesListSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(GetLearnerCoursesListError({ error: error }));
            })
          )
      )
    )
  );

  public updateLearnerCourseToggleEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateDisableLearnerCourse),
      switchMap((payload) =>
        this.learnerCourseService
          .updateLearnerCourseToggle$(payload.params)
          .pipe(
            map((result) => {
              return UpdateDisableLearnerCourseSuccess({
                params: result,
              });
            }),
            catchError((error: HttpErrorResponse) => {
              return of(UpdateDisableLearnerCourseError({ error: error }));
            })
          )
      )
    )
  );

  public reAssignLearnerToAssessmentEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(ReAssignLearnerToAssessment),
      switchMap((payload) => 
        this.learnerCourseLogService.reassignLearnerToInitialAssessment$(payload.courseId).pipe(
          map((result: number) => {
            return ReAssignLearnerToAssessmentSuccess({result: result});
          }),
          catchError((error) => {
            return of(ReAssignLearnerToAssessmentError({error: error}));
          })
        )
      )
    )
  );

  public generateLearningPlanEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GenerateLearningPlan),
      switchMap((payload) =>
        this.learnerCourseLogService.generateLearningPlan$(payload.courseId).pipe(
          map((result: number) => {
            return GenerateLearningPlanSuccess({result: result});
          }),
          catchError((error) => {
            return of(GenerateLearningPlanError({error: error}));
          })
        )
      )
    )
  );

  public learnerCourseLogEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearnerCourseLog),
      switchMap((payload) => 
        this.learnerCourseLogService.getLearnerCourseLogByLearnerAndCourseId$(payload.learnerId, payload.courseId).pipe(
          map((result: LearnerCourseLog) => {
            return GetLearnerCourseLogSuccess({result: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetLearnerCourseLogError({error: error}));
          })
        )  
      )
    )
  );

  public getByLearnerAndCourseIdEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearnerCourse),
      switchMap((payload) =>
        this.learnerCourseService
          .getByLearnerAndCourseId$(payload.learnerId, payload.courseId)
          .pipe(
            map((result: LearnerCourse) => {
              return GetLearnerCourseSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(GetLearnerCourseError({ error: error }));
            })
          )
      )
    )
  );

  public updateLearnerCourseEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(UpdateLearnerCourse),
      switchMap((payload) =>
        this.learnerCourseService
          .updateLearnerCourse$(payload.updated)
          .pipe(
            map((result: UpdateLearnerCourseDto) => {
              return UpdateLearnerCourseSuccess({ result: result });
            }),
            catchError((error: any) => {
              return of(UpdateLearnerCourseError({ error: error }));
            })
          )
      )
    )
  );

  public getLearnerCourseLogStatusEffect$ = createEffect(() =>
    this.action$.pipe(
      ofType(GetLearnersLearnerCourseLogStatus),
      switchMap((payload) => 
        this.learnerCourseLogService.getLearnerCourseLog$(payload.courseId)
          .pipe(
            map((result: LearnerCourseLog) => {
              return GetLearnersLearnerCourseLogStatusSuccess({result: result});
            }),
            catchError((error: HttpErrorResponse) => {
              return of(GetLearnersLearnerCourseLogStatusError({error: error}));
            })
          )
      )
    )
  );

  public assignCourseToMultipleLearnersEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(AddLearnerCourseList),
      switchMap((payload) => 
        this.learnerCourseService.addCourseToMultipleLearners$(payload.list).pipe(
          map((result: boolean) => {
            return AddLearnerCourseListSuccess({result: result});
          }),
          catchError((error) => {
            return of(AddLearnerCourseListError({error: error}));
          })
        )
      )
    )
  );

  public learnerCourseScreenerEffect$ = createEffect(() => 
    this.action$.pipe(
      ofType(GetLearnerCourseScreenerResult),
      switchMap((payload) => 
        this.learnerCourseService.getLearnerCourseScreenerResult$(payload.langId).pipe(
          map((result: Course[]) => {
            return GetLearnerCourseScreenerSuccess({courses: result});
          }),
          catchError((error: HttpErrorResponse) => {
            return of(GetLearnerCourseScreenerError({error: error}));
          })
        )
      )
    )
  );
}
